import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import QRCodeGenerator from '../../components/affiliate/QRCodeGenerator';

const AffiliateQRPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralCodes, setReferralCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [selectedCodeData, setSelectedCodeData] = useState(null);
  
  // Récupérer les codes de référence de l'affilié
  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchReferralCodes(user);
      } else {
        // Rediriger vers la page de connexion si non authentifié
        navigate('/affiliate/login');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);
  
  const fetchReferralCodes = async (user) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      const getAffiliateStats = httpsCallable(functions, 'getAffiliateStats');
      
      const result = await getAffiliateStats();
      const codes = result.data.referralCodes || [];
      
      setReferralCodes(codes);
      
      // Sélectionner le premier code par défaut s'il existe
      if (codes.length > 0) {
        setSelectedCode(codes[0].code);
        setSelectedCodeData(codes[0]);
      }
    } catch (error) {
      console.error('Error fetching affiliate stats:', error);
      setError(t('affiliate.qrpage.error.loadingCodes'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCodeChange = (e) => {
    const code = e.target.value;
    setSelectedCode(code);
    
    // Mettre à jour les données du code sélectionné
    const codeData = referralCodes.find(c => c.code === code);
    setSelectedCodeData(codeData);
  };
  
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <p>{t('affiliate.qrpage.loading')}</p>
      </LoadingContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        as={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1>{t('affiliate.qrpage.title')}</h1>
        <p>{t('affiliate.qrpage.subtitle')}</p>
      </PageHeader>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <ContentContainer>
        <QRCodeSection>
          <SelectCodeSection>
            <SelectCodeLabel htmlFor="referral-code">
              {t('affiliate.qrpage.selectCode')}:
            </SelectCodeLabel>
            <SelectCode
              id="referral-code"
              value={selectedCode}
              onChange={handleCodeChange}
            >
              {referralCodes.map(code => (
                <option key={code.code} value={code.code}>
                  {code.code} {code.description ? `- ${code.description}` : ''}
                </option>
              ))}
            </SelectCode>
          </SelectCodeSection>
          
          {selectedCode && (
            <QRCodeGenerator
              referralCode={selectedCode}
              appLink={`https://xenya-face.app/download?ref=${selectedCode}`}
            />
          )}
        </QRCodeSection>
        
        <MarketingSection>
          <h2>{t('affiliate.qrpage.marketing.title')}</h2>
          <p>{t('affiliate.qrpage.marketing.description')}</p>
          
          <MarketingIdeasList>
            <MarketingIdea>
              <IdeaIcon>🏪</IdeaIcon>
              <IdeaContent>
                <h3>{t('affiliate.qrpage.marketing.ideas.retail.title')}</h3>
                <p>{t('affiliate.qrpage.marketing.ideas.retail.description')}</p>
              </IdeaContent>
            </MarketingIdea>
            
            <MarketingIdea>
              <IdeaIcon>📱</IdeaIcon>
              <IdeaContent>
                <h3>{t('affiliate.qrpage.marketing.ideas.social.title')}</h3>
                <p>{t('affiliate.qrpage.marketing.ideas.social.description')}</p>
              </IdeaContent>
            </MarketingIdea>
            
            <MarketingIdea>
              <IdeaIcon>✉️</IdeaIcon>
              <IdeaContent>
                <h3>{t('affiliate.qrpage.marketing.ideas.email.title')}</h3>
                <p>{t('affiliate.qrpage.marketing.ideas.email.description')}</p>
              </IdeaContent>
            </MarketingIdea>
            
            <MarketingIdea>
              <IdeaIcon>🖨️</IdeaIcon>
              <IdeaContent>
                <h3>{t('affiliate.qrpage.marketing.ideas.print.title')}</h3>
                <p>{t('affiliate.qrpage.marketing.ideas.print.description')}</p>
              </IdeaContent>
            </MarketingIdea>
          </MarketingIdeasList>
        </MarketingSection>
      </ContentContainer>
      
      <ActionButtons>
        <BackButton onClick={() => navigate('/affiliate/dashboard')}>
          &larr; {t('affiliate.qrpage.backToDashboard')}
        </BackButton>
      </ActionButtons>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  h1 {
    margin-bottom: 10px;
    color: #333;
  }
  
  p {
    color: #666;
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffe0e0;
  color: #d32f2f;
  padding: 12px 20px;
  border-radius: 5px;
  margin-bottom: 20px;
  font-size: 0.9rem;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const QRCodeSection = styled.section`
`;

const SelectCodeSection = styled.div`
  margin-bottom: 20px;
`;

const SelectCodeLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const SelectCode = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    border-color: #6e8efb;
    outline: none;
  }
`;

const MarketingSection = styled.section`
  h2 {
    margin-bottom: 15px;
    color: #333;
  }
  
  p {
    color: #666;
    margin-bottom: 20px;
  }
`;

const MarketingIdeasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MarketingIdea = styled.div`
  display: flex;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const IdeaIcon = styled.div`
  font-size: 2rem;
  margin-right: 15px;
`;

const IdeaContent = styled.div`
  flex: 1;
  
  h3 {
    margin-bottom: 5px;
    color: #333;
    font-size: 1.1rem;
  }
  
  p {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #6e8efb;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.3s;
  
  &:hover {
    color: #a777e3;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  
  p {
    margin-top: 20px;
    color: #666;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6e8efb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default AffiliateQRPage;